/**
 * HTTP service: data access layer for CreditHQ REST API.
 * All URL building and parameterization; returns DAO types.
 * Auth via getAccessToken; no token endpoint here.
 */

import { fetchJsonWithAuth } from "../../api/fetchWithAuth"
import { getAccessToken } from "../../functions/functions"
import type {
	ChartOfAccountDAO,
	CreatePositionsResponseDAO,
	EntityDAO,
	FundAllocationDAO,
	FundDAO,
	FundParticipantDAO,
	FundParticipantDetailDAO,
	FundPipelineDAO,
	FundPipelineDetailDAO,
	FundTradeDAO,
	InstrumentGroupDAO,
	InstrumentDAO,
	PositionDAO,
	QuoteDAO,
	RateDAO,
	RateObservationDAO,
	SectorDAO,
	TradeDAO,
	ValuationDAO,
} from "./types"

const base = (b: string) => b.replace(/\/$/, "")

/** Builds URL: baseUrl + pathPrefix + "/" + each segment encoded. */
function buildUrl(
	baseUrl: string,
	pathPrefix: string,
	...segments: string[]
): string {
	const prefix = pathPrefix.startsWith("/") ? pathPrefix : `/${pathPrefix}`
	const rest = segments.map((s) => encodeURIComponent(s)).join("/")
	return `${base(baseUrl)}${prefix}${rest ? `/${rest}` : ""}`
}

function addQuery(url: string, params: Record<string, string | number | undefined>): string {
	const entries = Object.entries(params).filter(
		([, v]) => v !== undefined && v !== null && String(v).trim() !== "",
	) as [string, string | number][]
	if (entries.length === 0) return url
	const q = new URLSearchParams(
		entries.map(([k, v]) => [k, String(v)]),
	).toString()
	return `${url}?${q}`
}

async function fetchWithAuth(url: string): Promise<unknown> {
	return fetchJsonWithAuth(url, getAccessToken)
}

// ---------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------

const ENTITIES = "/api/v1/entities"

export async function getEntities(
	baseUrl: string,
	params?: { skip?: number; limit?: number; jq?: string },
): Promise<EntityDAO[]> {
	let url = `${base(baseUrl)}${ENTITIES}/`
	if (params) {
		const q: Record<string, string | number | undefined> = {}
		if (params.skip != null) q.skip = params.skip
		if (params.limit != null) q.limit = params.limit
		if (params.jq != null && params.jq.trim() !== "") q.jq = params.jq
		url = addQuery(url, q)
	}
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as EntityDAO[]) : []
}

export async function searchEntities(
	baseUrl: string,
	name: string,
	params?: { jq?: string },
): Promise<EntityDAO[]> {
	let url = buildUrl(baseUrl, ENTITIES, "search", name)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as EntityDAO[]) : []
}

export async function getEntityById(
	baseUrl: string,
	creditHqEntityIdentifier: string,
	params?: { jq?: string },
): Promise<EntityDAO> {
	let url = buildUrl(baseUrl, ENTITIES, "code", creditHqEntityIdentifier)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	return fetchWithAuth(url) as Promise<EntityDAO>
}

// ---------------------------------------------------------------------------
// Instrument groups
// ---------------------------------------------------------------------------

const INSTRUMENT_GROUPS = "/api/v1/instrument_groups"

export async function getInstrumentGroups(
	baseUrl: string,
	params?: { jq?: string },
): Promise<InstrumentGroupDAO[]> {
	let url = `${base(baseUrl)}${INSTRUMENT_GROUPS}/`
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as InstrumentGroupDAO[]) : []
}

export async function searchInstrumentGroups(
	baseUrl: string,
	name: string,
	params?: { jq?: string },
): Promise<InstrumentGroupDAO[]> {
	let url = buildUrl(baseUrl, INSTRUMENT_GROUPS, "search", name)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as InstrumentGroupDAO[]) : []
}

export async function getInstrumentGroupById(
	baseUrl: string,
	creditHqInstrumentGroupIdentifier: string,
	params?: { jq?: string },
): Promise<InstrumentGroupDAO> {
	let url = buildUrl(baseUrl, INSTRUMENT_GROUPS, creditHqInstrumentGroupIdentifier)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	return fetchWithAuth(url) as Promise<InstrumentGroupDAO>
}

// ---------------------------------------------------------------------------
// Instruments
// ---------------------------------------------------------------------------

const INSTRUMENTS = "/api/v1/instruments"

export async function getInstruments(
	baseUrl: string,
	params?: { jq?: string },
): Promise<InstrumentDAO[]> {
	let url = `${base(baseUrl)}${INSTRUMENTS}/`
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as InstrumentDAO[]) : []
}

export async function searchInstruments(
	baseUrl: string,
	name: string,
	params?: { jq?: string },
): Promise<InstrumentDAO[]> {
	let url = buildUrl(baseUrl, INSTRUMENTS, "search", name)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as InstrumentDAO[]) : []
}

export async function getInstrumentById(
	baseUrl: string,
	creditHqInstrumentIdentifier: string,
	params?: { jq?: string },
): Promise<InstrumentDAO> {
	let url = buildUrl(baseUrl, INSTRUMENTS, creditHqInstrumentIdentifier)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	return fetchWithAuth(url) as Promise<InstrumentDAO>
}

// ---------------------------------------------------------------------------
// Funds
// ---------------------------------------------------------------------------

const FUNDS = "/api/v1/funds"

export async function getFunds(
	baseUrl: string,
	params?: { jq?: string },
): Promise<FundDAO[]> {
	let url = `${base(baseUrl)}${FUNDS}/`
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as FundDAO[]) : []
}

export async function searchFunds(
	baseUrl: string,
	name: string,
	params?: { jq?: string },
): Promise<FundDAO[]> {
	let url = buildUrl(baseUrl, FUNDS, "search", name)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as FundDAO[]) : []
}

export async function getFundByCode(
	baseUrl: string,
	fundIdentifier: string,
	params?: { jq?: string },
): Promise<FundDAO> {
	let url = buildUrl(baseUrl, FUNDS, "code", fundIdentifier)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	return fetchWithAuth(url) as Promise<FundDAO>
}

// ---------------------------------------------------------------------------
// Fund trades
// ---------------------------------------------------------------------------

const FUND_TRADES = "/api/v1/fund_trades"

export async function getFundTrades(
	baseUrl: string,
	params?: { jq?: string },
): Promise<FundTradeDAO[]> {
	let url = `${base(baseUrl)}${FUND_TRADES}/`
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as FundTradeDAO[]) : []
}

export async function searchFundTradesByInstrument(
	baseUrl: string,
	instrumentIdentifier: string,
	params?: { jq?: string },
): Promise<FundTradeDAO[]> {
	let url = buildUrl(baseUrl, FUND_TRADES, "search", instrumentIdentifier)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as FundTradeDAO[]) : []
}

export async function getFundTradeByCode(
	baseUrl: string,
	creditHqFundTradeIdentifier: string,
	params?: { jq?: string },
): Promise<FundTradeDAO> {
	let url = buildUrl(baseUrl, FUND_TRADES, "code", creditHqFundTradeIdentifier)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	return fetchWithAuth(url) as Promise<FundTradeDAO>
}

// ---------------------------------------------------------------------------
// Fund allocations
// ---------------------------------------------------------------------------

const FUND_ALLOCATIONS = "/api/v1/fund_allocations"

export async function getFundAllocations(
	baseUrl: string,
	params?: { jq?: string },
): Promise<FundAllocationDAO[]> {
	let url = `${base(baseUrl)}${FUND_ALLOCATIONS}/`
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as FundAllocationDAO[]) : []
}

export async function searchFundAllocationsByFund(
	baseUrl: string,
	fundIdentifier: string,
	params?: { jq?: string },
): Promise<FundAllocationDAO[]> {
	let url = buildUrl(baseUrl, FUND_ALLOCATIONS, "search", fundIdentifier)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as FundAllocationDAO[]) : []
}

export async function getFundAllocationsByFundTradeCode(
	baseUrl: string,
	fundTradeIdentifier: string,
	params?: { jq?: string },
): Promise<FundAllocationDAO[]> {
	let url = buildUrl(baseUrl, FUND_ALLOCATIONS, "code", fundTradeIdentifier)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as FundAllocationDAO[]) : []
}

// ---------------------------------------------------------------------------
// Fund participants
// ---------------------------------------------------------------------------

const FUND_PARTICIPANTS = "/api/v1/fund_participants"

export async function getFundParticipants(
	baseUrl: string,
	params?: { jq?: string },
): Promise<FundParticipantDAO[]> {
	let url = `${base(baseUrl)}${FUND_PARTICIPANTS}/`
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as FundParticipantDAO[]) : []
}

export async function searchFundParticipants(
	baseUrl: string,
	name: string,
	params?: { jq?: string },
): Promise<FundParticipantDAO[]> {
	let url = buildUrl(baseUrl, FUND_PARTICIPANTS, "search", name)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as FundParticipantDAO[]) : []
}

export async function getFundParticipantsByFundCode(
	baseUrl: string,
	fundIdentifier: string,
	params?: { jq?: string },
): Promise<FundParticipantDetailDAO[]> {
	let url = buildUrl(baseUrl, FUND_PARTICIPANTS, "code", fundIdentifier)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as FundParticipantDetailDAO[]) : []
}

// ---------------------------------------------------------------------------
// Fund pipelines
// ---------------------------------------------------------------------------

const FUND_PIPELINES = "/api/v1/fund_pipelines"

export async function getFundPipelines(
	baseUrl: string,
	params?: { jq?: string },
): Promise<FundPipelineDAO[]> {
	let url = `${base(baseUrl)}${FUND_PIPELINES}/`
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as FundPipelineDAO[]) : []
}

export async function searchFundPipelines(
	baseUrl: string,
	name: string,
	params?: { jq?: string },
): Promise<FundPipelineDAO[]> {
	let url = buildUrl(baseUrl, FUND_PIPELINES, "search", name)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as FundPipelineDAO[]) : []
}

export async function getFundPipelineByCode(
	baseUrl: string,
	pipelineId: string,
	params?: { jq?: string },
): Promise<FundPipelineDetailDAO> {
	let url = buildUrl(baseUrl, FUND_PIPELINES, "code", pipelineId)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	return fetchWithAuth(url) as Promise<FundPipelineDetailDAO>
}

// ---------------------------------------------------------------------------
// Positions
// ---------------------------------------------------------------------------

const POSITIONS = "/api/v1/positions"

export async function getPositionsByFund(
	baseUrl: string,
	fundIdentifier: string,
	params?: { asof?: string; jq?: string },
): Promise<PositionDAO[]> {
	let url = buildUrl(baseUrl, POSITIONS, fundIdentifier)
	if (params) {
		const q: Record<string, string | undefined> = {}
		if (params.asof?.trim()) q.asof = params.asof
		if (params.jq?.trim()) q.jq = params.jq
		if (Object.keys(q).length) url = addQuery(url, q)
	}
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as PositionDAO[]) : []
}

export async function getPositionsByInstrument(
	baseUrl: string,
	instrumentIdentifier: string,
	params?: { jq?: string },
): Promise<PositionDAO[]> {
	let url = buildUrl(baseUrl, POSITIONS, "instrument", instrumentIdentifier)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as PositionDAO[]) : []
}

export async function createPositions(
	baseUrl: string,
	fundId: string,
	body: unknown,
): Promise<CreatePositionsResponseDAO> {
	const url = buildUrl(baseUrl, POSITIONS, fundId)
	const token = await getAccessToken()
	if (!token) throw new Error("Authentication required")
	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify(body),
	})
	if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`)
	return response.json() as Promise<CreatePositionsResponseDAO>
}

// ---------------------------------------------------------------------------
// Market data: rates
// ---------------------------------------------------------------------------

const RATES = "/api/v1/marketdata/rates"

export async function getRates(
	baseUrl: string,
	params?: { jq?: string },
): Promise<RateDAO[]> {
	let url = `${base(baseUrl)}${RATES}/`
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as RateDAO[]) : []
}

export async function searchRates(
	baseUrl: string,
	name: string,
	params?: { jq?: string },
): Promise<RateDAO[]> {
	let url = buildUrl(baseUrl, RATES, "search", name)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as RateDAO[]) : []
}

export async function getRateObservations(
	baseUrl: string,
	rateCode: string,
	params?: { jq?: string },
): Promise<RateObservationDAO[]> {
	let url = buildUrl(baseUrl, RATES, "code", rateCode, "observations")
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as RateObservationDAO[]) : []
}

// ---------------------------------------------------------------------------
// Market data: trades
// ---------------------------------------------------------------------------

const TRADES = "/api/v1/marketdata/trades"

export async function getTrades(
	baseUrl: string,
	params?: { jq?: string },
): Promise<TradeDAO[]> {
	let url = `${base(baseUrl)}${TRADES}/`
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as TradeDAO[]) : []
}

export async function getTradeByCode(
	baseUrl: string,
	creditHqTradeIdentifier: string,
	params?: { jq?: string },
): Promise<TradeDAO> {
	let url = buildUrl(baseUrl, TRADES, "code", creditHqTradeIdentifier)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	return fetchWithAuth(url) as Promise<TradeDAO>
}

export async function getTradesByInstrument(
	baseUrl: string,
	creditHqInstrumentIdentifier: string,
	params?: { jq?: string },
): Promise<TradeDAO[]> {
	let url = buildUrl(baseUrl, TRADES, "instrument", creditHqInstrumentIdentifier)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as TradeDAO[]) : []
}

// ---------------------------------------------------------------------------
// Market data: quotes
// ---------------------------------------------------------------------------

const QUOTES = "/api/v1/marketdata/quotes"

export async function getQuotesByInstrumentCode(
	baseUrl: string,
	creditHqInstrumentIdentifier: string,
	params?: { jq?: string },
): Promise<QuoteDAO[]> {
	let url = buildUrl(baseUrl, QUOTES, "code", creditHqInstrumentIdentifier)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as QuoteDAO[]) : []
}

// ---------------------------------------------------------------------------
// Market data: valuations
// ---------------------------------------------------------------------------

const VALUATIONS = "/api/v1/marketdata/valuations"

export async function getValuations(
	baseUrl: string,
	params?: { jq?: string },
): Promise<ValuationDAO[]> {
	let url = `${base(baseUrl)}${VALUATIONS}/`
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as ValuationDAO[]) : []
}

export async function getValuationsByInstrument(
	baseUrl: string,
	instrumentIdentifier: string,
	params?: { jq?: string },
): Promise<ValuationDAO[]> {
	let url = buildUrl(baseUrl, VALUATIONS, "instrument", instrumentIdentifier)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as ValuationDAO[]) : []
}

export async function getValuationByCode(
	baseUrl: string,
	valuationNumber: string,
	params?: { jq?: string },
): Promise<ValuationDAO> {
	let url = buildUrl(baseUrl, VALUATIONS, "code", valuationNumber)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	return fetchWithAuth(url) as Promise<ValuationDAO>
}

// ---------------------------------------------------------------------------
// Taxonomy: sectors
// ---------------------------------------------------------------------------

const SECTORS = "/api/v1/taxonomy/sectors"

export async function getSectors(
	baseUrl: string,
	params?: { jq?: string },
): Promise<SectorDAO[]> {
	let url = `${base(baseUrl)}${SECTORS}/`
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as SectorDAO[]) : []
}

export async function searchSectors(
	baseUrl: string,
	name: string,
	params?: { jq?: string },
): Promise<SectorDAO[]> {
	let url = buildUrl(baseUrl, SECTORS, "search", name)
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as SectorDAO[]) : []
}

// ---------------------------------------------------------------------------
// Chart of accounts
// ---------------------------------------------------------------------------

const COA = "/api/v1/CoA"

export async function getChartOfAccounts(
	baseUrl: string,
	params?: { jq?: string },
): Promise<ChartOfAccountDAO[]> {
	let url = `${base(baseUrl)}${COA}/`
	if (params?.jq?.trim()) url = addQuery(url, { jq: params.jq })
	const data = await fetchWithAuth(url)
	return Array.isArray(data) ? (data as ChartOfAccountDAO[]) : []
}

